import { hashStr } from '../../common-utils';
import { TYPES } from '../constants';
import { isTextSupported } from './wrap-text';
import { getNodeImageCache } from '../../provider/image';

export function getRenderCacheKey(stageContext, node) {
  const hasDynamicNode = hasDynamicNodeInSubtree(stageContext, node);
  const highCost = isHighRasterCost(stageContext, { w: node.w, h: node.h });
  const dirtyProvider = stageContext.providers.dirty;

  // Include a quantized zoom bucket so caches refresh promptly on zoom changes
  // while avoiding thrash on tiny deltas.
  // Use finer quantization (0.1 increments) to reduce blur during zoom
  let zoomBucket = '';
  try {
    const providers = stageContext.providers || {};
    const scaleProvider = providers.scale || { scale: 1 };
    const deviceDPR = stageContext.dpr || 1;
    const safeDPR = Math.max(2, deviceDPR);
    const zoom = (scaleProvider.scale || 1) * safeDPR;
    // Quantize zoom into 0.1 increments for better quality during zoom transitions
    // This ensures cache invalidation happens more frequently, preventing blur
    const bucket = Math.max(0.1, Math.round(zoom * 10) / 10);
    zoomBucket = `:Z${bucket.toFixed(2)}`;
  } catch {
    zoomBucket = '';
  }

  // Include descendant image readiness and cache identity in the key so that
  // when images finish loading (loading -> ready), the key changes and the
  // frame cache is refreshed.
  let imagesSignature = '';
  let nodeVersionSignature = '';
  try {
    const descendants = stageContext.api?.getDescendants
      ? stageContext.api.getDescendants(node.id)
      : [];
    const textProvider = stageContext.providers.text;
    if (descendants && descendants.length) {
      const parts = [];
      const nparts = [];
      for (const n of descendants) {
        parts.push(dirtyProvider.getVersion(n.id));
        if (n.type === TYPES.image) {
          parts.push(getImageStateKey(n));
        } else if (n.type === TYPES.text) {
          const isEditing = textProvider.isEditing(n);
          nparts.push(`E:${n.id}:${isEditing}`);
        }
      }
      imagesSignature = parts.length ? `:I${hashStr(parts.join('|'))}` : '';
      nodeVersionSignature = nparts.length ? `:N${hashStr(nparts.join('|'))}` : '';
    }
  } catch {
    // best effort only; ignore failures
    imagesSignature = '';
  }

  const imageKey = getImageStateKey(stageContext, node);
  const version = dirtyProvider.getVersion(node.id);

  const key = `#${version}:${imageKey}:${nodeVersionSignature}:F${hasDynamicNode ? 0 : 1}:H${highCost ? 1 : 0}${zoomBucket}:fill:${String(node.fill)}${imagesSignature}`;
  return key;
}

function getImageStateKey(stageContext, node) {
  const imgState = getNodeImageCache(stageContext, node.id);
  // loading: L, ready: R, unset: U
  const status = imgState ? (imgState.loading ? 'L' : imgState.img ? 'R' : 'U') : 'U';
  const cacheKey = imgState?.cacheKey || '';
  const dim = imgState?.img ? `${imgState.img.width}x${imgState.img.height}` : '';
  return `${node.id}:${status}:${cacheKey}:${dim}`;
}

const FILL_ANIMATED = 'animated=true';
const STRING = 'string';

export function hasDynamicNodeInSubtree(stageContext, groupNode) {
  const fill = groupNode.fill;
  if (typeof fill === STRING) {
    if (fill.includes(FILL_ANIMATED)) return true;
  }
  const descendants = stageContext.api.getDescendants(groupNode.id);
  for (const child of descendants) {
    if (child.type === TYPES.text) {
      const isEditing = stageContext.providers?.text?.isEditing(child);
      if (isEditing) {
        return true;
      }
      const textContent = child.text || '';
      if (textContent) {
        const loaded = isTextSupported(child, textContent, stageContext);
        if (!loaded) {
          return true;
        }
      }
    } else if (child.type === TYPES.image) {
      const cache = getNodeImageCache(stageContext, child.id);
      if (cache && cache.loading) {
        return true;
      }
    } else if (child.type === TYPES.shape) {
      if (typeof child.fill === STRING) {
        const loading = child.fill.includes(FILL_ANIMATED);
        if (loading) {
          return true;
        }
        const cache = getNodeImageCache(stageContext, child.id);
        if (cache && cache.loading) {
          return true;
        }
      }
    }
  }
  return false;
}

export function isHighRasterCost(stageContext, size) {
  const { w, h } = size;
  const providers = stageContext.providers || {};
  const scaleProvider = providers.scale || { scale: 1 };
  const deviceDPR = stageContext.dpr || 1;
  const safeDPR = Math.max(2, deviceDPR);
  const zoom = (scaleProvider.scale || 1) * safeDPR;

  const widthPx = Math.abs(w * zoom);
  const heightPx = Math.abs(h * zoom);
  const areaPx = widthPx * heightPx;

  // Heuristics: avoid caching when raster exceeds either dimension or area thresholds
  const MAX_DIMENSION = 4096; // px at raster scale
  const MAX_AREA = 16 * 1024 * 1024; // 16MP at raster scale

  return widthPx > MAX_DIMENSION || heightPx > MAX_DIMENSION || areaPx > MAX_AREA;
}

export function getCacheByType(renderContext, type) {
  if (!renderContext.cache) {
    renderContext.cache = {};
  }

  if (renderContext.cache.create) {
    return renderContext.cache.create(type);
  }

  if (!renderContext.cache[type]) {
    renderContext.cache[type] = new Map();
  }

  return renderContext.cache[type];
}

export function createRasterContext(renderContext, node, zoom) {
  if (zoom <= 0 || !isFinite(zoom)) {
    throw new Error('Invalid zoom value: zoom must be a positive finite number');
  }

  if (!node || typeof node.w !== 'number' || typeof node.h !== 'number') {
    throw new Error('Invalid node: node must have numeric width and height properties');
  }

  if (!isFinite(node.w) || !isFinite(node.h)) {
    throw new Error('Invalid node dimensions: width and height must be finite numbers');
  }

  // 确保最小缩放比例，避免低分辨率缓存
  const minZoom = Math.max(zoom, 0.2);
  const w = Math.abs(node.w * minZoom);
  const h = Math.abs(node.h * minZoom);

  const aspectRatio = w / h;
  const maxSize = 8192;

  let canvasWidth, canvasHeight;
  if (w > h) {
    canvasWidth = Math.min(w, maxSize);
    canvasHeight = canvasWidth / aspectRatio;
  } else {
    canvasHeight = Math.min(h, maxSize);
    canvasWidth = canvasHeight * aspectRatio;
  }

  // 确保最小尺寸，避免过小的缓存画布
  const minSize = 64;
  const finalWidth = Math.round(Math.max(minSize, canvasWidth));
  const finalHeight = Math.round(Math.max(minSize, canvasHeight));

  // 使用更精确的标准化尺寸，减少精度损失
  const standardizedWidth = getStandardizedSize(finalWidth);
  const standardizedHeight = getStandardizedSize(finalHeight);

  // Get canvas and context from pool with standardized dimensions
  const poolItem = renderContext.providers.canvas.createCanvas(
    standardizedWidth,
    standardizedHeight
  );
  const { canvas, context: cacheContext } = poolItem;

  // 创建渲染区域规范化配置
  const renderConfig = {
    actualWidth: finalWidth,
    actualHeight: finalHeight,
    canvasWidth: standardizedWidth,
    canvasHeight: standardizedHeight,
    scaleX: finalWidth / node.w,
    scaleY: finalHeight / node.h,
  };

  // 重置 cacheContext 的 transform 矩阵到初始状态
  cacheContext.setTransform(1, 0, 0, 1, 0, 0);
  // 清空整个canvas确保无残留内容
  cacheContext.clearRect(0, 0, standardizedWidth, standardizedHeight);

  // 设置实际渲染区域的裁剪
  cacheContext.beginPath();
  cacheContext.rect(0, 0, finalWidth, finalHeight);
  cacheContext.clip();

  // 应用缩放和平移变换
  cacheContext.scale(renderConfig.scaleX, renderConfig.scaleY);
  cacheContext.translate(-node.x, -node.y);

  return {
    canvas,
    context: cacheContext,
    zoomX: renderConfig.scaleX,
    zoomY: renderConfig.scaleY,
    renderConfig,
  };
}

// 将尺寸标准化为预定义的标准尺寸，提高canvas pool的命中率
function getStandardizedSize(size) {
  // 更精细的标准尺寸，减少精度损失
  const standardSizes = [
    32, 48, 64, 80, 96, 112, 128, 144, 160, 176, 192, 208, 224, 240, 256, 288, 320, 352, 384, 416,
    448, 480, 512, 576, 640, 704, 768, 832, 896, 960, 1024, 1152, 1280, 1408, 1536, 1664, 1792,
    1920, 2048, 2304, 2560, 2816, 3072, 3328, 3584, 3840, 4096, 4608, 5120, 5632, 6144, 6656, 7168,
    7680, 8192,
  ];

  // 找到最接近且大于等于目标尺寸的标准尺寸
  // 如果目标尺寸与标准尺寸的差异小于5%，直接使用目标尺寸
  for (let i = 0; i < standardSizes.length; i++) {
    if (standardSizes[i] >= size) {
      const diff = standardSizes[i] - size;
      const diffPercentage = diff / size;

      // 如果差异小于5%，使用原尺寸以避免不必要的内存浪费
      if (diffPercentage < 0.05) {
        return size;
      }
      return standardSizes[i];
    }
  }

  // 如果超出最大标准尺寸，使用原尺寸
  return size;
}

export function renderCacheable(renderContext, node, { render }) {
  const cacheKey = renderContext.providers.dirty.getVersion(node.id);
  const cache = (renderContext.cache = renderContext.cache || {});
  const RasterCache = (cache.rasterization = cache.rasterization || {});
  const isEditing = renderContext.providers.text.isEditing(node);
  const scale = renderContext.providers.scale.scale;
  if (isEditing) {
    return render(renderContext, node);
  }

  const cacheItem = RasterCache[node.id];
  if (cacheItem) {
    if (cacheItem.key === cacheKey && Math.abs(cacheItem.scale - scale) < 1) {
      const cachedCanvas = cacheItem.canvas;
      renderContext.context.drawImage(cachedCanvas, node.x, node.y, node.w, node.h);
      return;
    }
  }

  // Create off-screen canvas for rasterization
  const offscreenCanvas = document.createElement('canvas');
  const DPR = renderContext.dpr;
  const devicePixelRatio = DPR;
  const canvasWidth = Math.ceil(node.w * devicePixelRatio * scale);
  const canvasHeight = Math.ceil(node.h * devicePixelRatio * scale);

  offscreenCanvas.width = canvasWidth;
  offscreenCanvas.height = canvasHeight;
  offscreenCanvas.style.width = node.w + 'px';
  offscreenCanvas.style.height = node.h + 'px';

  const offscreenContext = offscreenCanvas.getContext('2d');
  offscreenContext.scale(devicePixelRatio * scale, devicePixelRatio * scale);

  // Create temporary render context for off-screen rendering
  const tempRenderContext = {
    ...renderContext,
    context: offscreenContext,
  };

  // Create temporary node with adjusted coordinates (relative to canvas)
  const tempNode = {
    ...node,
    x: 0,
    y: 0,
  };

  // Render text to off-screen canvas
  render(tempRenderContext, tempNode);

  // Cache the rasterized canvas
  RasterCache[node.id] = {
    scale,
    key: cacheKey,
    canvas: offscreenCanvas,
  };

  // Draw the cached canvas to main context
  renderContext.context.drawImage(offscreenCanvas, node.x, node.y, node.w, node.h);
}

// 缓存质量监控函数，用于调试缓存问题
export function monitorCacheQuality(renderContext, node) {
  const cacheName = node.id;
  const caches = getCacheByType(renderContext, 'renderText');
  const existCache = caches.get(cacheName);

  if (!existCache) {
    return {
      hasCache: false,
      quality: 'no_cache',
    };
  }

  const { scale } = renderContext.providers || {};
  const deviceDPR = renderContext.dpr;
  const safeDPR = Math.max(2, deviceDPR);
  const currentZoom = scale.scale * safeDPR;
  const zoomDiff = Math.abs(existCache.zoom - currentZoom);

  let quality = 'good';
  const issues = [];

  // 检查缩放匹配度
  if (zoomDiff > 0.1) {
    quality = 'poor';
    issues.push(`Zoom mismatch: ${zoomDiff.toFixed(3)}`);
  }

  // 检查缓存尺寸
  if (existCache.renderConfig) {
    const { actualWidth, actualHeight } = existCache.renderConfig;
    const expectedWidth = node.w * currentZoom;
    const expectedHeight = node.h * currentZoom;

    const widthDiff = Math.abs(actualWidth - expectedWidth) / expectedWidth;
    const heightDiff = Math.abs(actualHeight - expectedHeight) / expectedHeight;

    if (widthDiff > 0.05 || heightDiff > 0.05) {
      quality = 'poor';
      issues.push(`Size mismatch: width ${widthDiff.toFixed(3)}, height ${heightDiff.toFixed(3)}`);
    }
  }

  // 检查最小尺寸
  if (
    existCache.renderConfig &&
    (existCache.renderConfig.actualWidth < 64 || existCache.renderConfig.actualHeight < 64)
  ) {
    quality = 'poor';
    issues.push('Cache too small');
  }

  return {
    hasCache: true,
    quality,
    issues,
    zoom: existCache.zoom,
    currentZoom,
    zoomDiff,
    cacheSize: existCache.renderConfig
      ? {
          width: existCache.renderConfig.actualWidth,
          height: existCache.renderConfig.actualHeight,
        }
      : null,
  };
}
